import { Quote, MessageCircle } from "lucide-react";
import { Button } from "../ui/button";
import { openLiveChat } from "../../lib/chat";

const stories = [
  {
    quote:
      "I'd applied at three banks. All ghosted me. OliveUp called me back in 11 minutes and had funding wired in two days.",
    name: "Marisol R.",
    role: "Owner, Brooklyn Bakeshop",
    amount: "$182,000",
    product: "Working Capital",
    avatar:
      "https://images.pexels.com/photos/762020/pexels-photo-762020.jpeg?auto=compress&cs=tinysrgb&w=200&h=200&fit=crop",
  },
  {
    quote:
      "My credit took a hit during 2020. Three lenders said no. OliveUp said yes — and got me funded in two days. Game changer.",
    name: "Devon T.",
    role: "Co-founder, Triangle HVAC",
    amount: "$140,000",
    product: "Working Capital",
    avatar:
      "https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=200&h=200&fit=crop",
  },
  {
    quote:
      "My advisor Lori felt like a friend who happened to know everything about lending. We close on the new truck Monday.",
    name: "Sam K.",
    role: "Owner, Sam's Mobile Detail",
    amount: "$74,500",
    product: "Equipment Financing",
    avatar:
      "https://images.pexels.com/photos/1681010/pexels-photo-1681010.jpeg?auto=compress&cs=tinysrgb&w=200&h=200&fit=crop",
  },
];

const backedBusinesses = [
  {
    url: "https://images.pexels.com/photos/1839919/pexels-photo-1839919.jpeg?auto=compress&cs=tinysrgb&w=600&h=600&fit=crop",
    label: "Café",
  },
  {
    url: "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=600&h=600&fit=crop",
    label: "Studio",
  },
  {
    url: "https://images.pexels.com/photos/4350055/pexels-photo-4350055.jpeg?auto=compress&cs=tinysrgb&w=600&h=600&fit=crop",
    label: "Salon",
  },
  {
    url: "https://images.pexels.com/photos/1267324/pexels-photo-1267324.jpeg?auto=compress&cs=tinysrgb&w=600&h=600&fit=crop",
    label: "Construction",
  },
  {
    url: "https://images.pexels.com/photos/1058277/pexels-photo-1058277.jpeg?auto=compress&cs=tinysrgb&w=600&h=600&fit=crop",
    label: "Food truck",
  },
];

export default function Testimonials() {
  return (
    <section
      data-testid="testimonials-section"
      className="relative py-24 md:py-32"
    >
      <div className="container-tight">
        <div className="max-w-3xl mb-14">
          <span className="text-sm font-semibold uppercase tracking-[0.2em] text-primary">
            Stories From The Field
          </span>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-foreground leading-[1.05] text-balance">
            Real businesses. <br />Real high-fives.
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {stories.map((s, i) => (
            <article
              key={i}
              data-testid={`testimonial-${i}`}
              className="rounded-3xl bg-card border border-border p-8 flex flex-col transition-all hover:-translate-y-1 hover:shadow-[0_12px_40px_rgb(0,0,0,0.07)]"
            >
              <Quote className="h-7 w-7 text-accent" strokeWidth={2.2} />
              <p className="mt-5 text-base md:text-lg text-foreground leading-relaxed flex-1">
                &ldquo;{s.quote}&rdquo;
              </p>
              <div className="mt-7 pt-6 border-t border-border flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <img
                    src={s.avatar}
                    alt={s.name}
                    className="h-12 w-12 rounded-full object-cover border border-border shrink-0"
                    loading="lazy"
                  />
                  <div className="min-w-0">
                    <p className="font-semibold text-foreground truncate">{s.name}</p>
                    <p className="text-sm text-muted-foreground truncate">{s.role}</p>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-display font-extrabold text-primary text-lg">
                    {s.amount}
                  </p>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">
                    {s.product}
                  </p>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Backed businesses image strip */}
        <div className="mt-20">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-7">
            <div>
              <span className="text-sm font-semibold uppercase tracking-[0.2em] text-primary">
                50+ Industries Served
              </span>
              <h3 className="mt-3 font-display text-2xl md:text-3xl font-bold text-foreground">
                The kinds of small businesses we&apos;ve backed.
              </h3>
            </div>
            <p className="text-sm text-muted-foreground max-w-xs">
              Cafés. Salons. Shops. Rigs. Clinics. Studios. If you&apos;re
              building it, we&apos;re probably funding it.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {backedBusinesses.map((b, i) => (
              <div
                key={i}
                data-testid={`backed-image-${i}`}
                className="relative aspect-square rounded-2xl overflow-hidden bg-muted border border-border group"
              >
                <img
                  src={b.url}
                  alt={b.label}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/60 to-transparent">
                  <span className="text-xs font-semibold uppercase tracking-wider text-white">
                    {b.label}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div
          data-testid="testimonials-cta"
          className="mt-16 rounded-3xl border border-border bg-card px-6 py-8 md:px-10 md:py-9 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
        >
          <p className="font-display text-xl md:text-2xl font-bold text-foreground text-balance">
            Ready for your own story?
          </p>
          <p className="mt-2 text-sm text-muted-foreground max-w-lg mx-auto">
            Apply in a few minutes—or open chat if you want a human to size things up first.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button
              className="rounded-full h-12 px-8 bg-accent hover:bg-[#D1664A] text-accent-foreground font-semibold"
              onClick={() =>
                document.getElementById("apply")?.scrollIntoView({ behavior: "smooth" })
              }
              data-testid="testimonials-apply"
            >
              Get pre-qualified
            </Button>
            <Button
              type="button"
              variant="outline"
              className="rounded-full h-12 px-8 font-semibold border-primary/30 text-primary"
              onClick={openLiveChat}
              data-testid="testimonials-chat"
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              Live chat
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
